/**
 * HTML Sanitization Utility
 *
 * SOX Compliance: Prevents XSS (Cross-Site Scripting) attacks by sanitizing
 * all HTML content before rendering with dangerouslySetInnerHTML.
 *
 * Uses DOMPurify library which is the industry standard for XSS prevention.
 */

import DOMPurify from 'dompurify';

/**
 * Configuration for DOMPurify sanitization.
 * Allows safe HTML elements while blocking potentially dangerous ones.
 */
const SANITIZE_CONFIG: DOMPurify.Config = {
  // Allow common safe HTML tags
  ALLOWED_TAGS: [
    'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
    'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'hr', 'sub', 'sup', 'mark'
  ],
  // Allow safe attributes
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'class', 'style', 'id',
    'colspan', 'rowspan', 'scope'
  ],
  // Force all links to open in new tab with security attributes
  ADD_ATTR: ['target', 'rel'],
  // Forbid dangerous protocols in URLs
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
  // Remove scripts and other dangerous content
  FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
};

/**
 * Sanitize HTML content to prevent XSS attacks.
 *
 * SOX Compliance: All user-generated or external HTML content MUST be
 * sanitized before rendering to prevent malicious script injection.
 *
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * // Safe usage with dangerouslySetInnerHTML
 * <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(userContent) }} />
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) {
    return '';
  }

  // Sanitize the HTML using DOMPurify
  const clean = DOMPurify.sanitize(dirty, SANITIZE_CONFIG);

  // Post-process: Ensure all links have security attributes
  // This adds rel="noopener noreferrer" to prevent tabnabbing attacks
  return clean.replace(
    /<a\s+([^>]*?)>/gi,
    (_match: string, attrs: string) => {
      // Add target="_blank" if not present
      if (!attrs.includes('target=')) {
        attrs += ' target="_blank"';
      }
      // Add rel="noopener noreferrer" for security
      if (!attrs.includes('rel=')) {
        attrs += ' rel="noopener noreferrer"';
      } else if (!attrs.includes('noopener')) {
        attrs = attrs.replace(/rel="([^"]*)"/, 'rel="$1 noopener noreferrer"');
      }
      return `<a ${attrs}>`;
    }
  );
}

/**
 * Sanitize and format markdown-like content for display.
 * Converts basic markdown to HTML and then sanitizes.
 *
 * @param content - Content that may contain markdown
 * @returns Sanitized HTML string
 */
export function sanitizeMarkdown(content: string): string {
  if (!content) {
    return '';
  }

  // Convert basic markdown to HTML
  let html = content
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(/__(.*?)__/gim, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    .replace(/_(.*?)_/gim, '<em>$1</em>')
    // Code blocks
    .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`(.*?)`/gim, '<code>$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>')
    // Line breaks
    .replace(/\n/gim, '<br />');

  // Sanitize the converted HTML
  return sanitizeHtml(html);
}

/**
 * Strip all HTML tags from content, leaving only plain text.
 * Use this when you need text content without any HTML.
 *
 * @param html - HTML string to strip
 * @returns Plain text without any HTML
 */
export function stripHtml(html: string): string {
  if (!html) {
    return '';
  }
  // Use DOMPurify to strip all tags
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
}

/**
 * Check if a string contains potentially dangerous HTML.
 * Useful for validation before storing content.
 *
 * @param content - Content to check
 * @returns true if the content contains potentially dangerous HTML
 */
export function containsDangerousHtml(content: string): boolean {
  if (!content) {
    return false;
  }

  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,  // Event handlers like onclick=, onerror=
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<form/i,
    /data:/i,
  ];

  return dangerousPatterns.some(pattern => pattern.test(content));
}

const sanitizeUtils = {
  sanitizeHtml,
  sanitizeMarkdown,
  stripHtml,
  containsDangerousHtml,
};

export default sanitizeUtils;
